import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Card,
  Button,
  Typography,
  Row,
  Col,
  Table,
  Space,
  Tag,
  Input,
  Form,
  Select,
  InputNumber,
  Switch,
  notification,
  Popconfirm,
  Divider,
  Checkbox,
} from 'antd';
import {
  CloudServerOutlined,
  PlusOutlined,
  EditOutlined,
  CopyOutlined,
  DeleteOutlined,
  SaveOutlined,
  ArrowLeftOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const { Text, Title } = Typography;

const OPTION_CATEGORIES = {
  location: { label: 'Location', placeholder: 'e.g., Frankfurt, New York, Singapore' },
  vcpu: { label: 'CPU', placeholder: 'e.g., 2, 4, 8' },
  ram: { label: 'RAM', placeholder: 'e.g., 16GB, 32GB, 64GB' },
  gpu: { label: 'Streaming Quality', placeholder: 'e.g., 1080p, 2K, 4K' },
  storage: { label: 'Storage', placeholder: 'e.g., 512GB, 1TB' },
  os: { label: 'Operating System', placeholder: 'e.g., Ubuntu 22.04, Windows 10 (BYOL)' },
  networking: { label: 'Networking', placeholder: 'e.g., 1Gbps, 10Gbps' },
  system_disk: { label: 'System Disk', placeholder: 'e.g., 50GB SSD' },
  data_disk: { label: 'Data Disk', placeholder: 'e.g., 200GB SSD' },
};

const OPTION_GROUPS_BY_TYPE = {
  desktop: ['location', 'vcpu', 'gpu', 'storage', 'os'], // Cloud PC
  server: ['vcpu', 'ram', 'networking', 'system_disk', 'data_disk'], // Cloud VPS
};

const getTypeLabel = (type) => {
  if (type === 'desktop') return 'Cloud PC';
  if (type === 'server') return 'Cloud VPS';
  return type?.toUpperCase() || '-';
};

function ResourceManagement({
  embedded = false,
  onUpdated,
  onRefresh,
  resourcesData,
  loadingData,
}) {
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [form] = Form.useForm();
  const [selectedOptions, setSelectedOptions] = useState({
    vcpu: [],
    ram: [],
    gpu: [],
    storage: [],
    os: [],
    location: [],
  });
  const selectedResourceType = Form.useWatch('type', form) || editingResource?.type || 'desktop';
  const activeOptionGroups = OPTION_GROUPS_BY_TYPE[selectedResourceType] || OPTION_GROUPS_BY_TYPE.desktop;

  const fetchData = useCallback(async () => {
    if (embedded && typeof onRefresh === 'function') {
      await onRefresh();
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      if (!embedded) navigate('/login');
      return;
    }

    try {
      setLoading(true);
      const resourcesRes = await axios.get(`${API_URL}/api/resources?lite=1&available=0`);
      const resourcesData = Array.isArray(resourcesRes.data?.data)
        ? resourcesRes.data.data
        : (Array.isArray(resourcesRes.data) ? resourcesRes.data : []);
      setResources(resourcesData);
    } catch (err) {
      notification.error({
        message: 'Failed to load resources',
        description: err.response?.data?.message || err.message,
      });
    } finally {
      setLoading(false);
    }
  }, [navigate, embedded, onRefresh]);

  useEffect(() => {
    if (!embedded) {
      fetchData();
    }
  }, [fetchData, embedded]);

  const resetEditor = useCallback((keepOpen = false) => {
    setEditingResource(null);
    setShowEditor(keepOpen);
    form.resetFields();
    form.setFieldsValue({ type: 'desktop' });
    setSelectedOptions({ vcpu: [], ram: [], gpu: [], storage: [], os: [], location: [] });
  }, [form]);

  const notifyParent = useCallback(() => {
    if (onUpdated) onUpdated();
  }, [onUpdated]);

  const displayedResources = embedded && Array.isArray(resourcesData)
    ? resourcesData
    : resources;
  const displayedLoading = embedded && typeof loadingData === 'boolean'
    ? loadingData
    : loading;

  const [resourcesPage, setResourcesPage] = useState(1);
  const resourcesPageSize = 10;
  const pagedResources = displayedResources.slice(
    (resourcesPage - 1) * resourcesPageSize,
    resourcesPage * resourcesPageSize,
  );

  const handleSubmit = async (values) => {
    const token = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
      const resourceData = {
        name: values.name,
        type: values.type,
        base_price_per_hour: parseFloat(values.base_price_per_hour) || 0,
        available: values.available !== undefined ? values.available : true,
      };

      if (editingResource) {
        await axios.put(`${API_URL}/api/resources/${editingResource.id}`, resourceData, config);

        const existingComponentsRes = await axios.get(
          `${API_URL}/api/resources/${editingResource.id}/components`,
          config,
        );
        const existingComponents = Array.isArray(existingComponentsRes.data)
          ? existingComponentsRes.data
          : [];

        await Promise.all(
          existingComponents.map((comp) =>
            axios.delete(`${API_URL}/api/resources/components/${comp.id}`, config),
          ),
        );

        for (const [category, options] of Object.entries(selectedOptions)) {
          for (const option of options) {
            await axios.post(`${API_URL}/api/resources/${editingResource.id}/components`, {
              name: option.name,
              type: category,
              value: option.value,
              price_delta: option.price_delta || 0,
              is_free: option.is_free || false,
              is_default: option.is_default || false,
            }, config);
          }
        }

        notification.success({ message: 'Resource updated successfully' });
      } else {
        const res = await axios.post(`${API_URL}/api/resources`, resourceData, config);
        const newResourceId = res.data.id;

        for (const [category, options] of Object.entries(selectedOptions)) {
          for (const option of options) {
            await axios.post(`${API_URL}/api/resources/${newResourceId}/components`, {
              name: option.name,
              type: category,
              value: option.value,
              price_delta: option.price_delta || 0,
              is_free: option.is_free || false,
              is_default: option.is_default || false,
            }, config);
          }
        }

        notification.success({ message: 'Resource created successfully' });
      }

      await fetchData();
      notifyParent();
      resetEditor(false);
    } catch (err) {
      notification.error({
        message: 'Operation failed',
        description: err.response?.data?.message || err.message,
      });
    }
  };

  const handleDelete = async (id) => {
    const token = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
      await axios.delete(`${API_URL}/api/resources/${id}`, config);
      notification.success({ message: 'Resource deleted successfully' });
      await fetchData();
      notifyParent();
    } catch (err) {
      notification.error({
        message: 'Deletion failed',
        description: err.response?.data?.message || err.message,
      });
    }
  };

  const handleEdit = async (record) => {
    setEditingResource(record);
    setShowEditor(true);
    form.setFieldsValue({
      name: record.name,
      type: record.type,
      base_price_per_hour: parseFloat(record.base_price_per_hour || 0),
      available: record.available,
    });

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const componentsRes = await axios.get(`${API_URL}/api/resources/${record.id}/components`, config);
      const components = componentsRes.data || [];

      const grouped = { vcpu: [], ram: [], gpu: [], storage: [], os: [], location: [] };
      components.forEach((comp, idx) => {
        let category = comp.type || 'other';
        if (category === 'vcpus') category = 'vcpu';
        if (category === 'ssd') category = 'storage';
        if (!grouped[category]) grouped[category] = [];
        grouped[category].push({ ...comp, id: comp.id || idx });
      });

      Object.keys(grouped).forEach((category) => {
        let defaultFound = false;
        grouped[category] = grouped[category].map((opt) => {
          if (!opt.is_default) return opt;
          if (!defaultFound) {
            defaultFound = true;
            return opt;
          }
          return { ...opt, is_default: false };
        });
      });

      setSelectedOptions(grouped);
    } catch (err) {
      notification.error({ message: 'Failed to load resource options' });
    }
  };

  const handleDuplicate = async (record) => {
    const token = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const resourceRes = await axios.get(`${API_URL}/api/resources/${record.id}`, config);
      const resource = resourceRes.data;

      const resourceData = {
        name: `${resource.name} Copy`,
        type: resource.type,
        location: resource.location,
        vcpus: resource.vcpus,
        ram: resource.ram,
        ssd: resource.ssd,
        gpu_model: resource.gpu_model,
        stream_capabilities: resource.stream_capabilities,
        os: resource.os,
        ip: resource.ip,
        gpu_comparison: resource.gpu_comparison,
        min_vcpus: resource.min_vcpus,
        min_ram: resource.min_ram,
        additional_pricing: resource.additional_pricing,
        specs: resource.specs,
        base_price_per_hour: parseFloat(resource.base_price_per_hour || 0),
        available: resource.available,
      };

      const created = await axios.post(`${API_URL}/api/resources`, resourceData, config);
      const newResourceId = created.data.id;

      const componentsRes = await axios.get(`${API_URL}/api/resources/${record.id}/components`, config);
      const components = componentsRes.data || [];
      for (const comp of components) {
        await axios.post(`${API_URL}/api/resources/${newResourceId}/components`, {
          name: comp.name,
          type: comp.type,
          value: comp.value,
          price_delta: comp.price_delta || 0,
          is_free: comp.is_free || false,
          is_default: comp.is_default || false,
        }, config);
      }

      notification.success({ message: 'Resource duplicated successfully' });
      await fetchData();
      notifyParent();
    } catch (err) {
      notification.error({
        message: 'Duplicate failed',
        description: err.response?.data?.message || err.message,
      });
    }
  };

  const addOption = (category, option) => {
    setSelectedOptions((prev) => {
      const existing = prev[category] || [];
      const normalizedExisting = option.is_default
        ? existing.map((opt) => ({ ...opt, is_default: false }))
        : existing;

      return {
        ...prev,
        [category]: [...normalizedExisting, { ...option, id: Date.now() + Math.random() }],
      };
    });
  };

  const removeOption = (category, optionId) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [category]: prev[category].filter((opt) => opt.id !== optionId),
    }));
  };

  const resourceColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color={type === 'gpu' ? 'purple' : type === 'server' ? 'blue' : 'green'}>
          {getTypeLabel(type)}
        </Tag>
      ),
    },
    {
      title: 'Base Config',
      key: 'baseConfig',
      render: (_, record) => (
        <Space size="small" direction="vertical">
          {record.gpu_model && <Text type="secondary">GPU: {record.gpu_model}</Text>}
          {record.vcpus && <Text type="secondary">{record.vcpus} vCPUs</Text>}
          {record.ram && <Text type="secondary">{record.ram} RAM</Text>}
        </Space>
      ),
    },
    {
      title: 'Price/hr',
      dataIndex: 'base_price_per_hour',
      key: 'base_price_per_hour',
      render: (price) => <Text type="success" strong>€{parseFloat(price || 0).toFixed(2)}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'available',
      key: 'available',
      render: (available) => (
        <Tag color={available ? 'green' : 'red'}>
          {available ? 'Available' : 'Unavailable'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
          <Space size="small">
            <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small">Edit</Button>
            <Button icon={<CopyOutlined />} onClick={() => handleDuplicate(record)} size="small">Duplicate</Button>
            <Popconfirm title="Delete this resource?" onConfirm={() => handleDelete(record.id)} okText="Yes" cancelText="No">
              <Button icon={<DeleteOutlined />} danger size="small">Delete</Button>
            </Popconfirm>
          </Space>
        ),
      },
    ];

  const content = (
    <Row gutter={[24, 24]}>
      <Col xs={24}>
        <Card
          className="glass"
          title={<Space><DatabaseOutlined /> Resource Inventory</Space>}
          extra={
            <Space className="resource-toolbar">
              <Button type="primary" icon={<PlusOutlined />} className="resource-btn-primary" onClick={() => resetEditor(true)}>
                Add New Resource
              </Button>
              <Button className="resource-btn-ghost" onClick={fetchData}>Refresh</Button>
            </Space>
          }
        >
          <Table
            columns={resourceColumns}
            dataSource={displayedResources}
            rowKey="id"
            loading={displayedLoading}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 1000 }}
            className="custom-table resource-table"
          />

          <div className="resource-cards">
            {pagedResources.map((record) => (
              <div key={record.id} className="resource-card glass">
                <div className="resource-card-head">
                  <div>
                    <Text strong className="resource-card-title">{record.name}</Text>
                    <div className="resource-card-type">
                      <Tag color={record.type === 'gpu' ? 'purple' : record.type === 'server' ? 'blue' : 'green'}>
                        {getTypeLabel(record.type)}
                      </Tag>
                    </div>
                  </div>
                  <Tag color={record.available ? 'green' : 'red'}>
                    {record.available ? 'Available' : 'Unavailable'}
                  </Tag>
                </div>

                <div className="resource-card-body">
                  <div className="resource-card-line">
                    <span className="resource-card-label">Base config</span>
                    <span className="resource-card-value">
                      {record.gpu_model && `GPU: ${record.gpu_model} `}
                      {record.vcpus && `| ${record.vcpus} vCPUs `}
                      {record.ram && `| ${record.ram}`}
                    </span>
                  </div>
                  <div className="resource-card-line">
                    <span className="resource-card-label">Price/hr</span>
                    <span className="resource-card-value">€{parseFloat(record.base_price_per_hour || 0).toFixed(2)}</span>
                  </div>
                </div>

                <div className="resource-card-actions">
                  <Button icon={<EditOutlined />} size="small" className="resource-btn-ghost" onClick={() => handleEdit(record)}>Edit</Button>
                  <Button icon={<CopyOutlined />} size="small" className="resource-btn-ghost" onClick={() => handleDuplicate(record)}>Duplicate</Button>
                  <Popconfirm title="Delete this resource?" onConfirm={() => handleDelete(record.id)} okText="Yes" cancelText="No">
                    <Button icon={<DeleteOutlined />} danger size="small" className="resource-btn-danger">Delete</Button>
                  </Popconfirm>
                </div>
              </div>
            ))}
          </div>

          <div className="resource-pagination">
            <button
              className="orders-page-btn"
              onClick={() => setResourcesPage((p) => Math.max(1, p - 1))}
              disabled={resourcesPage === 1}
              aria-label="Previous page"
            >
              {'<'}
            </button>
            <span className="orders-page-info">
              {resourcesPage} / {Math.max(1, Math.ceil(displayedResources.length / resourcesPageSize))}
            </span>
            <button
              className="orders-page-btn"
              onClick={() => setResourcesPage((p) => Math.min(Math.ceil(displayedResources.length / resourcesPageSize), p + 1))}
              disabled={resourcesPage >= Math.ceil(displayedResources.length / resourcesPageSize)}
              aria-label="Next page"
            >
              {'>'}
            </button>
          </div>
        </Card>
      </Col>

      {showEditor && (
      <Col xs={24}>
        <Card
          className="glass resource-management-form"
          title={editingResource ? `Edit Resource: ${editingResource.name}` : 'Add New Resource'}
          extra={editingResource && <Tag color="blue">Editing Mode</Tag>}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ type: 'desktop', available: true, base_price_per_hour: 0 }}
          >
            <Divider orientation="left">Basic Information</Divider>
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item name="name" label="Resource Name" rules={[{ required: true, message: 'Please enter resource name' }]}>
                  <Input placeholder="e.g., RTX 4090 Gaming Server" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="type" label="Resource Type" rules={[{ required: true }]}>
                  <Select>
                    <Select.Option value="desktop">Cloud PC</Select.Option>
                    <Select.Option value="server">Cloud VPS</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item name="base_price_per_hour" label="Base Price per Hour (€)" rules={[{ required: true }]}>
                  <InputNumber min={0} step={0.01} precision={2} addonAfter="€" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="available" valuePropName="checked" label="Available for ordering">
                  <Switch checkedChildren="Available" unCheckedChildren="Unavailable" />
                </Form.Item>
              </Col>
            </Row>


            <Divider orientation="left">Available Options</Divider>
            {activeOptionGroups.map((category) => {
              const info = OPTION_CATEGORIES[category];
              const label = selectedResourceType === 'desktop' && category === 'vcpu'
                ? 'CPU and RAM'
                : info.label;
              const placeholder = selectedResourceType === 'desktop' && category === 'vcpu'
                ? 'e.g., 4 vCPU, 16GB RAM'
                : info.placeholder;
              const hasDefault = (selectedOptions[category] || []).some((opt) => opt.is_default);
              return (
              <Card key={category} size="small" style={{ marginBottom: 16 }} title={<Space>{label}{selectedOptions[category]?.length > 0 && <Tag color="green">{selectedOptions[category].length} selected</Tag>}</Space>}>
                {selectedOptions[category]?.length > 0 ? (
                  <div style={{ marginBottom: 16 }}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>Selected {label} options:</Text>
                    <Space wrap>
                      {selectedOptions[category].map((opt) => (
                        <Tag key={opt.id} closable onClose={() => removeOption(category, opt.id)} color="blue" style={{ padding: '4px 8px' }}>
                          {opt.value}
                          {opt.price_delta > 0 && <Text type="warning"> (+€{opt.price_delta})</Text>}
                          {opt.price_delta < 0 && <Text type="danger"> (€{opt.price_delta})</Text>}
                          {opt.is_free && <Text type="success"> (Free)</Text>}
                          {opt.is_default && <Text type="success"> (Default)</Text>}
                        </Tag>
                      ))}
                    </Space>
                  </div>
                ) : (
                  <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                    No {info.label.toLowerCase()} options selected. Click "Add Option" below.
                  </Text>
                )}

                  <OptionAdder
                    categoryInfo={{ ...info, label, placeholder }}
                    hasDefault={hasDefault}
                    onAdd={(option) => addOption(category, option)}
                  />
                </Card>
                );
              })}

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                  {editingResource ? 'Update Resource' : 'Create Resource'}
                </Button>
                <Button onClick={() => resetEditor(false)}>Close</Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </Col>
      )}
    </Row>
  );

  if (embedded) {
    return <div className="resource-management resource-management-embedded">{content}</div>;
  }

  return (
    <div className="resource-management resource-management-standalone" style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '24px' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin')} style={{ marginBottom: '16px' }}>
          Back to Admin
        </Button>
        <Title level={2}><CloudServerOutlined /> Resource Management</Title>
        <Text type="secondary">Manage GPU resources and configure available options for each resource</Text>
      </div>
      {content}
    </div>
  );
}

function OptionAdder({ categoryInfo, hasDefault, onAdd }) {
  const [value, setValue] = useState('');
  const [priceDelta, setPriceDelta] = useState(0);
  const [isFree, setIsFree] = useState(false);
  const [isDefault, setIsDefault] = useState(false);

  const handleAdd = () => {
    if (!value.trim()) return;

    onAdd({
      value: value.trim(),
      price_delta: isFree ? 0 : priceDelta,
      is_free: isFree,
      is_default: isDefault,
      name: `${categoryInfo.label}: ${value.trim()}`,
    });

    setValue('');
    setPriceDelta(0);
    setIsFree(false);
    setIsDefault(false);
  };

  return (
    <Row gutter={12} align="middle">
      <Col xs={24} md={10} lg={12}>
        <Input
          placeholder={`e.g., ${categoryInfo.placeholder}`}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onPressEnter={handleAdd}
        />
      </Col>
      <Col xs={24} sm={12} md={6} lg={4}>
        <InputNumber
          min={-100}
          max={100}
          step={0.01}
          precision={2}
          addonAfter="€"
          value={priceDelta}
          onChange={(val) => setPriceDelta(val || 0)}
          style={{ width: 140 }}
          disabled={isFree}
        />
      </Col>
      <Col xs={24} sm={12} md={8}>
        <Space wrap>
          <Checkbox checked={isFree} onChange={(e) => setIsFree(e.target.checked)}>Free</Checkbox>
          <Checkbox
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
            disabled={hasDefault && !isDefault}
          >
            Default
          </Checkbox>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} disabled={!value.trim()}>
            Add Option
          </Button>
        </Space>
      </Col>
    </Row>
  );
}

export default ResourceManagement;

